import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, TOKEN_KEY, USER_KEY, fmtDate, COMBUSTIVEL_LABEL, formatError } from "@/lib/api";
import { Check, X as XIcon, Clock, AlertTriangle, ShieldCheck, LogIn, KeyRound } from "lucide-react";
import { toast } from "sonner";

/**
 * Página de aprovação de combustível.
 * Fluxo: Login → Carregar dados → Aprovar/Rejeitar (exige PIN).
 */
export default function FuelApprove() {
  const { token } = useParams();
  const [phase, setPhase] = useState("loading"); // loading | login | needs-pin-setup | ready
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [busy, setBusy] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [pinModal, setPinModal] = useState(null); // 'approve' | 'reject' | null
  const [pin, setPin] = useState("");
  const [me, setMe] = useState(null);

  const authHeaders = () => {
    const t = localStorage.getItem(TOKEN_KEY);
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const fetchMe = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { headers: authHeaders() });
      setMe(data);
      return data;
    } catch { return null; }
  };

  const fetchData = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) { setPhase("login"); return; }
    try {
      const { data } = await axios.get(`${API}/public/fuel/${token}`, { headers: { Authorization: `Bearer ${t}` } });
      setData(data);
      const u = await fetchMe();
      if (u && !u.has_pin && (u.role === "tesoureiro" || u.role === "admin" || u.role === "presidencia")) {
        setPhase("needs-pin-setup");
      } else {
        setPhase("ready");
      }
      setError("");
    } catch (e) {
      const code = e.response?.status;
      if (code === 401 || code === 403) {
        localStorage.removeItem(TOKEN_KEY);
        setPhase("login");
        return;
      }
      setError(e.response?.data?.detail || "Link inválido ou expirado");
      setPhase("error");
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, loginForm);
      localStorage.setItem(TOKEN_KEY, data.access_token);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      toast.success(`Olá, ${data.user.name}!`);
      fetchData();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "E-mail ou senha incorretos");
    } finally { setBusy(false); }
  };

  const confirmAction = async () => {
    if (!pin || pin.length < 4) { toast.error("Digite seu PIN (4 a 6 dígitos)"); return; }
    if (pinModal === "reject" && !observacoes.trim()) { toast.error("Informe o motivo da rejeição"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/public/fuel/${token}/${pinModal}`,
        { observacoes, pin },
        { headers: authHeaders() });
      toast.success(pinModal === "approve" ? "Solicitação aprovada!" : "Solicitação rejeitada");
      setPinModal(null); setPin("");
      fetchData();
    } catch (err) {
      const detail = formatError(err.response?.data?.detail);
      toast.error(detail || "Falha ao processar");
      // Limpa o PIN para nova tentativa
      setPin("");
    } finally { setBusy(false); }
  };

  if (phase === "loading") return <div className="min-h-screen bg-[#F4F8F5] flex items-center justify-center font-mono-data text-sm">Carregando…</div>;

  if (phase === "error") return (
    <div className="min-h-screen bg-[#F4F8F5] flex items-center justify-center p-4">
      <div className="brutal-card max-w-md w-full p-6 text-center">
        <AlertTriangle className="w-12 h-12 mx-auto text-[#E60000] mb-3" />
        <div className="font-display font-black text-xl uppercase mb-2">Link inválido</div>
        <div className="text-sm text-[#555]">{error}</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F4F8F5] p-4">
      <div className="max-w-2xl mx-auto py-6">
        <div className="bg-[#3D6B57] text-white p-4 border-2 border-[#0A1F14]">
          <div className="font-mono-data text-[10px] uppercase tracking-widest opacity-80">Cooperativa Mista da FLONA do Tapajós</div>
          <div className="font-display font-black text-2xl uppercase tracking-tight">COOMFLONA · Combustível</div>
          <div className="font-mono-data text-[10px] opacity-80 mt-1">CNPJ 07.315.165/0001-00</div>
        </div>

        {phase === "login" && (
          <div className="brutal-card p-6">
            <div className="flex items-center gap-3 mb-4 pb-3 border-b-2 border-[#111]">
              <LogIn className="w-6 h-6 text-[#FF4F00]" />
              <div>
                <div className="font-display font-black text-xl uppercase">Acesso restrito</div>
                <div className="text-xs text-[#555]">Apenas Tesouraria / Presidência / Admin podem visualizar e aprovar.</div>
              </div>
            </div>
            <form onSubmit={doLogin} className="space-y-3">
              <div>
                <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">E-mail</label>
                <input type="email" required value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} className="w-full border-2 border-[#111] p-2 text-sm" autoFocus data-testid="fuel-login-email" />
              </div>
              <div>
                <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Senha</label>
                <input type="password" required value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} className="w-full border-2 border-[#111] p-2 text-sm" data-testid="fuel-login-pwd" />
              </div>
              <button type="submit" disabled={busy} className="w-full px-4 py-3 bg-[#FF4F00] text-white font-display font-bold uppercase text-sm tracking-wider disabled:opacity-50" data-testid="fuel-login-submit">
                {busy ? "Entrando…" : "Entrar"}
              </button>
            </form>
          </div>
        )}

        {phase === "needs-pin-setup" && (
          <PinSetupCard onDone={fetchData} authHeaders={authHeaders} />
        )}

        {phase === "ready" && data && (
          <>
            <div className="brutal-card p-5">
              <div className="flex items-start justify-between gap-3 mb-4 pb-3 border-b-2 border-[#111]">
                <div>
                  <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#777]">Protocolo</div>
                  <div className="font-display font-black text-2xl">{data.protocolo}</div>
                </div>
                <StatusBadge status={data.status} />
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                <Row label="Motorista" value={data.motorista_nome} />
                <Row label="Documento" value={data.motorista_documento || "—"} />
                <Row label="Veículo" value={data.veiculo || "—"} />
                <Row label="Placa" value={data.placa} mono />
                <Row label="Combustível" value={COMBUSTIVEL_LABEL[data.tipo_combustivel] || data.tipo_combustivel} />
                <Row label="Quantidade" value={`${data.quantidade} L`} mono />
                <Row label="Hodômetro" value={`${data.hodometro_atual} km`} mono />
                <Row label="Unidade" value={data.filial === "km117" ? "KM 117 Anambé" : (data.filial === "cupari" ? "Rio Cuparí" : "—")} />
                <Row label="Solicitada por" value={data.requester_name} />
                <Row label="Criada em" value={fmtDate(data.created_at)} mono />
                <Row label="Expira em" value={fmtDate(data.expires_at)} mono />
                {data.approved_at && <Row label="Aprovada em" value={fmtDate(data.approved_at)} mono />}
                {data.approved_by_name && <Row label="Aprovador" value={data.approved_by_name} />}
              </div>

              {data.justificativa && (
                <div className="mb-4">
                  <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Justificativa</div>
                  <div className="border-2 border-[#111] p-2 text-sm bg-[#F4F8F5]">{data.justificativa}</div>
                </div>
              )}

              {data.observacoes_aprovador && (
                <div className="mb-4">
                  <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Observações do Aprovador</div>
                  <div className="border-2 border-[#3D6B57] p-2 text-sm bg-[#E8F0EA]">{data.observacoes_aprovador}</div>
                </div>
              )}

              {data.rejected_reason && (
                <div className="mb-4">
                  <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#E60000] mb-1">Motivo da Rejeição</div>
                  <div className="border-2 border-[#E60000] p-2 text-sm bg-[#FEE]">{data.rejected_reason}</div>
                </div>
              )}

              {data.status === "pendente" && (
                <>
                  <textarea
                    value={observacoes}
                    onChange={(e) => setObservacoes(e.target.value)}
                    placeholder="Observação (obrigatória ao rejeitar)…"
                    className="w-full border-2 border-[#111] p-2 text-sm mb-3"
                    rows={3}
                    data-testid="fuel-obs"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => { setPin(""); setPinModal("approve"); }} disabled={busy} className="px-4 py-3 bg-[#1A7F37] text-white font-display font-bold uppercase text-sm tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="fuel-approve">
                      <Check className="w-5 h-5" />Aprovar
                    </button>
                    <button onClick={() => { setPin(""); setPinModal("reject"); }} disabled={busy} className="px-4 py-3 bg-[#E60000] text-white font-display font-bold uppercase text-sm tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="fuel-reject">
                      <XIcon className="w-5 h-5" />Rejeitar
                    </button>
                  </div>
                  <div className="mt-3 text-[11px] text-[#777] text-center">🔒 Será solicitado seu <b>PIN pessoal</b> para confirmar a ação</div>
                </>
              )}

              {data.status === "aprovado" && (
                <div className="border-2 border-[#3D6B57] p-4 text-center">
                  <ShieldCheck className="w-10 h-10 mx-auto text-[#3D6B57] mb-2" />
                  <div className="font-display font-bold uppercase text-sm">QR Code de Abastecimento</div>
                  <div className="font-mono-data text-[10px] text-[#555] mb-3">Válido para uso único · não compartilhe</div>
                  <img src={`${API}/public/fuel/${token}/qr.png`} alt="QR" className="mx-auto" style={{ maxWidth: 220 }} />
                  {data.qr_used_at && <div className="mt-2 font-mono-data text-xs text-[#E60000]"><Clock className="w-3 h-3 inline mr-1" />QR já utilizado em {fmtDate(data.qr_used_at)}</div>}
                </div>
              )}
            </div>

            {/* PIN modal */}
            {pinModal && (
              <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => !busy && setPinModal(null)}>
                <div className="bg-white border-2 border-[#111] max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b-2 border-[#111]">
                    <KeyRound className="w-5 h-5 text-[#FF4F00]" />
                    <div className="font-display font-bold uppercase text-sm tracking-wider">
                      Digite seu PIN para {pinModal === "approve" ? "aprovar" : "rejeitar"}
                    </div>
                  </div>
                  <input
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoFocus
                    maxLength={6}
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                    onKeyDown={(e) => { if (e.key === "Enter") confirmAction(); }}
                    placeholder="••••"
                    className="w-full border-2 border-[#111] p-3 text-2xl text-center font-mono-data tracking-[0.5em]"
                    data-testid="pin-input"
                  />
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <button onClick={() => setPinModal(null)} disabled={busy} className="px-3 py-2 border-2 border-[#111] font-display font-bold uppercase text-xs tracking-wider">Cancelar</button>
                    <button onClick={confirmAction} disabled={busy} className="px-3 py-2 bg-[#FF4F00] text-white font-display font-bold uppercase text-xs tracking-wider disabled:opacity-50" data-testid="pin-confirm">
                      {busy ? "Validando…" : "Confirmar"}
                    </button>
                  </div>
                  <div className="text-[10px] text-[#777] mt-2">3 tentativas erradas bloqueiam por 5 min.</div>
                </div>
              </div>
            )}
          </>
        )}

        <div className="text-center font-mono-data text-[10px] uppercase tracking-widest text-[#555] mt-4">
          COOMFLONA · Sistema de Almoxarifado · Token expira em 24h · 🔒 Login + PIN
        </div>
      </div>
    </div>
  );
}

function PinSetupCard({ onDone, authHeaders }) {
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pin.length < 4 || pin !== pin2) { toast.error("PIN com 4-6 dígitos e confirmação igual"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/set-pin`, { pin }, { headers: authHeaders() });
      toast.success("PIN definido! Use-o para aprovar/rejeitar.");
      onDone();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <div className="brutal-card p-6">
      <div className="flex items-center gap-3 mb-3 pb-3 border-b-2 border-[#111]">
        <KeyRound className="w-6 h-6 text-[#FF4F00]" />
        <div>
          <div className="font-display font-black text-lg uppercase">Defina seu PIN</div>
          <div className="text-xs text-[#555]">Você precisa de um PIN para aprovar/rejeitar requisições.</div>
        </div>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <input type="password" inputMode="numeric" maxLength={6} placeholder="Novo PIN (4-6 dígitos)" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} className="w-full border-2 border-[#111] p-2 text-lg text-center font-mono-data tracking-widest" autoFocus data-testid="pin-setup-1" />
        <input type="password" inputMode="numeric" maxLength={6} placeholder="Confirme o PIN" value={pin2} onChange={(e) => setPin2(e.target.value.replace(/\D/g, ""))} className="w-full border-2 border-[#111] p-2 text-lg text-center font-mono-data tracking-widest" data-testid="pin-setup-2" />
        <button type="submit" disabled={busy} className="w-full px-4 py-3 bg-[#FF4F00] text-white font-display font-bold uppercase text-sm tracking-wider disabled:opacity-50" data-testid="pin-setup-submit">
          {busy ? "Salvando…" : "Salvar PIN"}
        </button>
      </form>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div>
      <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#777]">{label}</div>
      <div className={mono ? "font-mono-data font-bold text-sm" : "font-medium text-sm"}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pendente: { bg: "bg-[#FFC107] text-[#111]", t: "PENDENTE" },
    aprovado: { bg: "bg-[#1A7F37] text-white", t: "APROVADO" },
    rejeitado: { bg: "bg-[#E60000] text-white", t: "REJEITADO" },
    utilizado: { bg: "bg-[#3D6B57] text-white", t: "UTILIZADO" },
    expirado: { bg: "bg-[#777] text-white", t: "EXPIRADO" },
  };
  const c = map[status] || map.pendente;
  return <div className={`${c.bg} px-3 py-1.5 font-display font-bold uppercase text-xs tracking-widest`}>{c.t}</div>;
}
